import { Suspense, lazy, useEffect, useState } from 'react'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { GlobalTooltip } from '@/components/ui/global-tooltip'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ErrorBoundary } from '@/app/error-boundary'
import { PwaInstallPrompt } from '@/app/pwa-install-prompt'
import { RouteErrorScreen } from '@/app/route-error'
import { WorkspaceGate } from '@/features/workspace-gate/workspace-gate'
import { routeTree } from './routeTree.gen'

// Route errors (thrown from beforeLoad/loader) never reach the React
// ErrorBoundary below — TanStack catches them first. Without this, they render
// its untranslated built-in fallback with the message hidden in production.
const router = createRouter({ routeTree, defaultErrorComponent: RouteErrorScreen })
const LazyToaster = lazy(async () => {
  const { Toaster } = await import('@/components/ui/sonner')
  return { default: Toaster }
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// =====================================================================
// Aero session guard — verifies the page was navigated to by the Aero
// host app (signed session token), not opened directly in a browser.
// =====================================================================

const AERO_PUBLIC_KEY_PEM = `-----BEGIN RSA PUBLIC KEY-----
MIIBCgKCAQEAxsSvDD6jdtzMOQ3xODNNFbbtUnHaRWZ9PIFOn5emg5yUXrDHiNcH
3WE2yYSmhNLygFMkhuNbKhv0t7yucHEqDKVGkYsuoCNSQmXSU3FFy0c0lUVyaxSH
pmFUA+68XzihanNPnj0B3rZl0UNBQ72Xl6Kn0Te3KXkoi2Ms3kU9sPxlLQnQBP1S
1HwIUQQWIRA2bHZwc4HdssxWbu976jqu7RjCLJbMjXYE/kSA/JwvS7ZXp7yY8Bos
SZnB97DL9C9aULMumy7CtK1OM9emxNhezCx4R6tbExNZTryZS5f29X5fM7XKPu43
EWcRxeDoZ++SuWRQGdAu1yhnXd9014P9uQIDAQAB
-----END RSA PUBLIC KEY-----`

type AeroSessionPayload = { aud: string; stamp: number; nonce: string }

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

function base64UrlToBytes(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  return base64ToBytes(
    normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '='),
  )
}

function derLength(length: number): number[] {
  if (length < 128) return [length]
  const bytes: number[] = []
  let value = length
  while (value > 0) {
    bytes.unshift(value & 0xff)
    value >>= 8
  }
  return [0x80 | bytes.length, ...bytes]
}

function derSequence(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0)
  const header = new Uint8Array([0x30, ...derLength(total)])
  const output = new Uint8Array(header.length + total)
  output.set(header, 0)
  let offset = header.length
  for (const part of parts) {
    output.set(part, offset)
    offset += part.length
  }
  return output
}

function derBitString(value: Uint8Array): Uint8Array {
  const header = new Uint8Array([0x03, ...derLength(value.length + 1), 0x00])
  const output = new Uint8Array(header.length + value.length)
  output.set(header, 0)
  output.set(value, header.length)
  return output
}

function publicKeyPemToSpki(pem: string): Uint8Array {
  const clean = pem
    .replace('-----BEGIN RSA PUBLIC KEY-----', '')
    .replace('-----END RSA PUBLIC KEY-----', '')
    .replace(/\s+/g, '')
  const pkcs1 = base64ToBytes(clean)
  const rsaEncryptionAlgorithm = new Uint8Array([
    0x30, 0x0d, 0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01, 0x05, 0x00,
  ])
  return derSequence(rsaEncryptionAlgorithm, derBitString(pkcs1))
}

async function importAeroPublicKey(): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'spki',
    publicKeyPemToSpki(AERO_PUBLIC_KEY_PEM),
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['verify'],
  )
}

async function validateSignedSession(expectedAudience: string): Promise<boolean> {
  const cacheKey = `aero_runtime_ok_${expectedAudience}`
  if (sessionStorage.getItem(cacheKey) === '1') return true

  const session = new URLSearchParams(window.location.search).get('session')
  if (!session) return false

  const parts = session.split('.')
  if (parts.length !== 2) return false

  const payloadB64 = parts[0]
  const signatureB64 = parts[1]
  const payloadText = new TextDecoder().decode(base64UrlToBytes(payloadB64))
  const payload = JSON.parse(payloadText) as AeroSessionPayload

  if (payload.aud !== expectedAudience) return false
  if (!payload.stamp || !payload.nonce) return false

  const now = Math.floor(Date.now() / 1000)
  if (Math.abs(now - payload.stamp) > 30) return false

  const key = await importAeroPublicKey()
  const valid = await crypto.subtle.verify(
    'RSASSA-PKCS1-v1_5',
    key,
    base64UrlToBytes(signatureB64),
    new TextEncoder().encode(payloadB64),
  )

  if (valid) sessionStorage.setItem(cacheKey, '1')
  return valid
}

function AeroLocked() {
  return (
    <div className="w-screen h-screen flex items-center justify-center bg-black text-white text-sm">
      Aero Preview
    </div>
  )
}

export function App() {
  const [isAero, setIsAero] = useState<boolean | null>(null)
  const [showToaster, setShowToaster] = useState(false)

  useEffect(() => {
    if (import.meta.env.DEV) {
      setIsAero(true)
      return
    }

    validateSignedSession('editor')
      .then(setIsAero)
      .catch(() => setIsAero(false))
  }, [])

  // Prevent default browser zoom application-wide
  useEffect(() => {
    const wheelListenerOptions: AddEventListenerOptions = { passive: false, capture: true }
    const keyListenerOptions: AddEventListenerOptions = { capture: true }

    const preventBrowserZoom = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
      }
    }

    const preventKeyboardZoom = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === '+' || e.key === '=' || e.key === '-' || e.key === '_' || e.key === '0') {
          e.preventDefault()
        }
      }
    }

    document.addEventListener('wheel', preventBrowserZoom, wheelListenerOptions)
    document.addEventListener('keydown', preventKeyboardZoom, keyListenerOptions)

    return () => {
      document.removeEventListener('wheel', preventBrowserZoom, wheelListenerOptions)
      document.removeEventListener('keydown', preventKeyboardZoom, keyListenerOptions)
    }
  }, [])

  useEffect(() => {
    const show = () => setShowToaster(true)
    window.addEventListener('freecut:ensure-toaster', show)
    return () => {
      window.removeEventListener('freecut:ensure-toaster', show)
    }
  }, [])

  if (isAero === null) {
    return <AeroLocked />
  }

  if (!isAero) {
    return <AeroLocked />
  }

  return (
    <ErrorBoundary level="app">
      <TooltipProvider delayDuration={300}>
        <WorkspaceGate>
          <RouterProvider router={router} />
        </WorkspaceGate>
        <GlobalTooltip />
        <PwaInstallPrompt />
        {showToaster && (
          <Suspense fallback={null}>
            <LazyToaster />
          </Suspense>
        )}
      </TooltipProvider>
    </ErrorBoundary>
  )
}